import React from 'react';
import { Box, Typography, Grid, Card, CardContent, CircularProgress, Alert, Button } from '@mui/joy';
import { ChartBar, Briefcase, CurrencyDollar, UsersThree, CalendarBlank, ShieldCheck } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { useDashboardData } from '../../hooks/useDashboard';
import { StatsCard } from '../../components/dashboard/StatsCard';
import { JobStatusChart } from '../../components/dashboard/JobStatusChart';
import { SalesPipelineChart } from '../../components/dashboard/SalesPipelineChart';
import { RecentTicketsCard } from '../../components/dashboard/RecentTicketsCard';
import { DashboardSetupReminder } from '../../components/setup/DashboardSetupReminder';
import { isAffiliate, isSuperAdmin } from '../../utils/affiliate';
import { useNavigate } from 'react-router-dom';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';

export const DashboardApp: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { data: dashboardData, isLoading: dataLoading, error } = useDashboardData();
  const navigate = useNavigate();

  if (authLoading || dataLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">
          Failed to load dashboard data. Please try refreshing the page.
        </Alert>
      </Box>
    );
  }

  if (!user) {
    // This should be handled by AuthProvider, but just in case
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">
          Please log in to view your dashboard.
        </Alert>
      </Box>
    );
  }

  const stats = dashboardData?.stats || {
    total_jobs: 0,
    jobs_this_week: 0,
    jobs_this_month: 0,
    total_value: 0,
    total_contacts: 0,
    contacts_this_week: 0,
  };

  return (
    <Box sx={{ width: '95%', mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography level="h1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChartBar size={30} weight="duotone" /> Dashboard
          <PageHelpButton />
        </Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          Welcome back, {user.first_name || user.username}! Here's what's happening with your business.
        </Typography>
      </Box>

      {/* Setup Reminder (shown until setup is complete) */}
      <DashboardSetupReminder />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Jobs"
            value={stats.total_jobs}
            change={`${stats.jobs_this_week} this week`}
            changeType="positive"
            icon={<Briefcase size={30} weight="duotone" />}
            color="primary"
          />
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Value"
            value={`$${stats.total_value.toLocaleString()}`}
            change="All active jobs"
            changeType="neutral"
            icon={<CurrencyDollar size={30} weight="duotone" />}
            color="success"
          />
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Contacts"
            value={stats.total_contacts}
            change={`${stats.contacts_this_week} this week`}
            changeType="positive"
            icon={<UsersThree size={30} weight="duotone" />}
            color="info"
          />
        </Grid>
        <Grid xs={12} sm={6} lg={3}>
          <StatsCard
            title="Jobs This Month"
            value={stats.jobs_this_month}
            change="Last 30 days"
            changeType="neutral"
            icon={<CalendarBlank size={30} weight="duotone" />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Job Status & Sales Pipeline Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Job Status Chart */}
        <Grid xs={12} lg={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography level="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChartBar size={22} weight="duotone" /> Job Status Breakdown
              </Typography>
              <JobStatusChart
                boards={dashboardData?.job_boards || []}
                data={dashboardData?.job_status_breakdown || {}}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Sales Pipeline Status */}
        <Grid xs={12} lg={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography level="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Briefcase size={22} weight="duotone" /> Sales Pipeline Status Breakdown
              </Typography>
              <SalesPipelineChart
                boards={dashboardData?.opportunity_boards || []}
                data={dashboardData?.opportunity_pipeline_breakdown || {}}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Super Admin: Support Center + Admin Center (bottom row) */}
      {isSuperAdmin(user) && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid xs={12} lg={9}>
            <RecentTicketsCard />
          </Grid>
          <Grid xs={12} lg={3}>
            <Card variant="outlined" sx={{ height: '100%', backgroundColor: 'background.level1' }}>
              <CardContent>
                <Typography level="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShieldCheck size={22} weight="duotone" /> Admin Center
                </Typography>
                <Typography level="body-md" sx={{ mb: 2, color: 'text.secondary' }}>
                  Manage affiliate program and system administration.
                </Typography>
                <Button
                  variant="solid"
                  color="warning"
                  onClick={() => navigate('/manage/affiliates')}
                  size="sm"
                >
                  Manage Affiliates
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Affiliate Quick Actions */}
      {isAffiliate(user) && (
        <Box sx={{ mb: 4 }}>
          <Card variant="outlined" sx={{ backgroundColor: 'background.level1' }}>
            <CardContent>
              <Typography level="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CurrencyDollar size={22} weight="duotone" /> Affiliate Center
              </Typography>
              <Typography level="body-md" sx={{ mb: 2, color: 'text.secondary' }}>
                Manage your affiliate program and track your earnings.
              </Typography>
              <Button
                variant="solid"
                color="success"
                onClick={() => navigate('/affiliate-dashboard')}
                size="sm"
              >
                Go to Affiliate Dashboard
              </Button>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Become an Affiliate Prompt */}
      {!isAffiliate(user) && !isSuperAdmin(user) && (
        <Box sx={{ mb: 4 }}>
          <Card variant="outlined" sx={{ backgroundColor: 'background.level1' }}>
            <CardContent>
              <Typography level="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                🤝 Join Our Affiliate Program
              </Typography>
              <Typography level="body-md" sx={{ mb: 2, color: 'text.secondary' }}>
                Earn commissions by referring other roofing contractors to ApexOS.
              </Typography>
              <Button
                variant="solid"
                color="primary"
                onClick={() => navigate('/affiliate-signup')}
                size="sm"
              >
                Become an Affiliate
              </Button>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};
